import { HdBodygraphSide, HdGateBlockPinV1 } from '../hd-bodygraph.models';

/**
 * Datamodel voor de gate-planeetblokjes-laag. Alle coördinaten zijn in
 * **viewBox-eenheden** van de bodygraph-svg (`0..1182` × `0..1772`), net als de
 * annotatielaag, zodat de blokjes meeschalen en mee printen.
 * Spec: docs/superpowers/specs/2026-07-05-hd-gate-planeetblokjes-design.md
 */

/** Planeetactivaties bestaan enkel op deze twee zijden ('highlight' niet). */
export type HdGateBlockSide = Extract<HdBodygraphSide, 'design' | 'personality'>;

export interface HdGateBlockRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** Eén te renderen blokje (nog zonder positie). */
export interface HdGateBlockDescriptor {
  readonly gate: number;
  readonly planetId: number;
  readonly side: HdGateBlockSide;
  /** Poort ligt in een gedefinieerd center → gevulde variant. */
  readonly isDefined: boolean;
  /** Astronomicon-glyph van de planeet. */
  readonly glyph: string;
}

/** Alle blokjes van één poort, in vaste sorteervolgorde (spec §3). */
export interface HdGateBlockCluster {
  readonly gate: number;
  readonly blocks: readonly HdGateBlockDescriptor[];
}

/**
 * Handmatig vastgezet blokje — runtime-zicht (readonly) op het persistente
 * preset-type {@link HdGateBlockPinV1}; offsets relatief aan het poort-anker.
 */
export type HdGateBlockPin = Readonly<HdGateBlockPinV1>;

export interface HdGateBlockPlacement {
  readonly descriptor: HdGateBlockDescriptor;
  readonly rect: HdGateBlockRect;
  readonly pinned: boolean;
}

/** Recht kanaalsegment tussen de ankers van de twee poorten (spec §5). */
export interface HdGateBlockChannelSegment {
  readonly key: string;
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
  readonly length: number;
}

export interface HdGateBlockLayoutInput {
  readonly clusters: readonly HdGateBlockCluster[];
  readonly gateAnchors: ReadonlyMap<number, HdGateBlockRect>;
  readonly channelSegments: readonly HdGateBlockChannelSegment[];
  readonly pins: readonly HdGateBlockPin[];
}

/**
 * Blokmaat in viewBox-eenheden (spec §3). Iets groter dan een poort-disc
 * (~44u): de chart rendert door de 72vh-cap op Full HD op ~44% schaal en de
 * glyph moet ook dáár leesbaar blijven (52u ≈ 23 schermpixels op 1080p,
 * ~30px op 1440p — vergelijkbaar met het referentieprogramma).
 */
export const HD_GATE_BLOCK_SIZE = 52;
/** Tussenruimte tussen blokjes binnen een cluster. */
export const HD_GATE_BLOCK_GAP = 2;
export const HD_GATE_BLOCK_VIEWBOX_WIDTH = 1182;
export const HD_GATE_BLOCK_VIEWBOX_HEIGHT = 1772;

/** Magneet-tolerantie van het sleep-snappen, in viewBox-eenheden (spec §8). */
export const HD_GATE_BLOCK_SNAP_TOLERANCE = 6;
/** Verplaatsing (viewBox-eenheden) waaronder een pointerup als klik telt, niet als sleep. */
export const HD_GATE_BLOCK_DRAG_THRESHOLD = 4;

/** Stabiele identiteit van een blokje (gate + planeet + zijde). */
export function hdGateBlockKey(d: Pick<HdGateBlockDescriptor, 'gate' | 'planetId' | 'side'>): string {
  return `${d.gate}:${d.planetId}:${d.side}`;
}
